import { Controller } from "@hotwired/stimulus";

/**
 * ModelTypewriter Controller
 *
 * Creates a typewriter effect that cycles through model names in the code example.
 * Deletes the current model character by character, then types the new one.
 * Supports multiple model targets (e.g., for SDK switcher with multiple code blocks).
 */
export default class extends Controller<HTMLElement> {
  static targets = ["model"];
  static values = {
    models: { type: Array, default: [] },
    interval: { type: Number, default: 3000 },
    typeSpeed: { type: Number, default: 50 },
    deleteSpeed: { type: Number, default: 30 },
    pauseDelay: { type: Number, default: 1500 },
  };

  declare readonly modelTargets: HTMLElement[];
  declare readonly hasModelTarget: boolean;
  declare modelsValue: string[];
  declare intervalValue: number;
  declare typeSpeedValue: number;
  declare deleteSpeedValue: number;
  declare pauseDelayValue: number;

  private currentIndex: number = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isAnimating: boolean = false;

  connect(): void {
    if (!this.hasModelTarget || this.modelsValue.length === 0) return;

    // Start the cycle after the initial interval
    this.intervalId = setInterval(() => {
      this.cycleModel();
    }, this.intervalValue);
  }

  disconnect(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private async cycleModel(): Promise<void> {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Get next model
    this.currentIndex = (this.currentIndex + 1) % this.modelsValue.length;
    const newModel = this.modelsValue[this.currentIndex];
    const currentText = this.modelTargets[0]?.textContent || "";

    // Delete current text (from all targets simultaneously)
    await this.deleteText(currentText);

    // Brief pause before typing new text
    await this.pause(200);

    // Type new text (to all targets simultaneously)
    await this.typeText(newModel);

    // Pause to let the user read the new model before cycling
    await this.pause(this.pauseDelayValue);

    this.isAnimating = false;
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private deleteText(text: string): Promise<void> {
    return new Promise((resolve) => {
      let index = text.length;

      const deleteChar = () => {
        if (index > 0) {
          index--;
          const newText = text.substring(0, index);
          // Update all model targets simultaneously
          this.modelTargets.forEach((target) => {
            target.textContent = newText;
          });
          setTimeout(deleteChar, this.deleteSpeedValue);
        } else {
          resolve();
        }
      };

      deleteChar();
    });
  }

  private typeText(text: string): Promise<void> {
    return new Promise((resolve) => {
      let index = 0;

      const typeChar = () => {
        if (index < text.length) {
          const newText = text.substring(0, index + 1);
          // Update all model targets simultaneously
          this.modelTargets.forEach((target) => {
            target.textContent = newText;
          });
          index++;
          setTimeout(typeChar, this.typeSpeedValue);
        } else {
          resolve();
        }
      };

      typeChar();
    });
  }
}
